import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { showToast } from 'components/common/CustomToast';
import ParkingContext from 'context/ParkingContext';
import { isAfter } from 'date-fns';
import { READ_RESUMABLE_PARKING_SESSION } from 'graphql/queries/readResumableParkingSession';
import { useContext, useEffect, useMemo } from 'react';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useParkingStore } from 'stores/parkingStore';
import {
  Query,
  QueryReadResumableParkingSessionArgs,
  ResumableParkingInfo,
} from 'types/generatedSchemaTypes';
import { emptyQuery } from 'utils/commonUtils';
import { getCustomerSegmentUid } from 'utils/parkUtils';
import useSelectedParkingVehicle from './useSelectedParkingVehicle';

const useResumableParkingSession = () => {
  const selectedVehicle = useSelectedParkingVehicle();
  const { licensePlate } = selectedVehicle || {};
  const currentParkingZone = useActiveElementStore(
    (state) => state.activeElement
  );
  const { parkingZoneResult } = useContext(ParkingContext) || {};
  const hasResumableSession = useParkingStore(
    (state) => state.resumableSession
  );
  const selectedCustomerSegment = useParkzoneStore(
    (state) => state.selectedTariffId
  );
  const currentSegment = getCustomerSegmentUid(selectedCustomerSegment);

  const parkingAbuseFixEnabled =
    parkingZoneResult?.data?.parkZone?.parkingMethods?.parkingAbuseFix;

  const [readResumableParkingSession, { data } = emptyQuery] = useLazyQuery<
    Query,
    QueryReadResumableParkingSessionArgs
  >(READ_RESUMABLE_PARKING_SESSION, {
    fetchPolicy: 'no-cache',
  });

  // Hook for interval to reset the resumableSession IF user has resumableSession AND has set valid until in onComplete
  useEffect(() => {
    let interval: any = null;
    if (hasResumableSession) {
      const expireTime = new Date(
        hasResumableSession?.resumeAllowedUntil as Date
      );
      interval = setInterval(() => {
        const dateNow = new Date();
        if (isAfter(dateNow, expireTime)) {
          useParkingStore.getState().setResumableSession(undefined);
          showToast('parking.resumeParkingSession.sessionExpired', 'info');
        }
      }, 1000);
      return () => clearInterval(interval);
    } else {
      return () => clearInterval(interval);
    }
  }, [hasResumableSession]);

  useEffect(() => {
    if (
      currentParkingZone &&
      licensePlate?.plateText &&
      parkingAbuseFixEnabled
    ) {
      readResumableParkingSession({
        variables: {
          input: {
            parkingZoneUid: currentParkingZone,
            licensePlate: licensePlate.plateText,
            customerSegmentUid: currentSegment,
          },
        },
      });
    }
  }, [
    readResumableParkingSession,
    currentParkingZone,
    licensePlate?.plateText,
    parkingAbuseFixEnabled,
    currentSegment,
  ]);

  const memoizedSession = useMemo(() => {
    return data?.readResumableParkingSession?.resumableSession;
  }, [data?.readResumableParkingSession?.resumableSession]);

  useEffect(() => {
    useParkingStore
      .getState()
      .setResumableSession(memoizedSession as ResumableParkingInfo);
  }, [memoizedSession]);
};

export default useResumableParkingSession;
